import { useState, useEffect } from 'react';
import { reportBuilderService } from '../../services/reportBuilderService';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  showToast,
} from '@t-one-internal/design-system';
import { CircleNotch, CloudArrowUp, Trash } from '@phosphor-icons/react';

interface PublishAsWidgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportId: string;
  reportName: string;
  reportDescription?: string;
  chartType?: string;
}

interface PublishStatus {
  is_published: boolean;
  data?: {
    id: string;
    title: string;
    description: string;
    chart_type: string;
    scoping: string;
    layout_default_width: number;
    layout_default_height: number;
    is_enabled: boolean;
  };
}

// Valid widget chart types — must match the <option> values below
const WIDGET_CHART_TYPES = ['table', 'bar', 'line', 'pie', 'donut', 'area', 'horizontal_bar', 'stacked_bar'];

// Ensure a chart type value is valid for the widget system
function toWidgetChartType(type: string | undefined): string {
  if (!type) return 'table';
  if (WIDGET_CHART_TYPES.includes(type)) return type;
  // Map report builder names → widget names
  if (type === 'column') return 'bar';
  if (type === 'radar') return 'bar';
  return 'table';
}

export default function PublishAsWidgetDialog({
  open,
  onOpenChange,
  reportId,
  reportName,
  reportDescription = '',
  chartType: defaultChartType,
}: PublishAsWidgetDialogProps) {
  const [title, setTitle] = useState(reportName);
  const [description, setDescription] = useState(reportDescription);
  const [chartType, setChartType] = useState(toWidgetChartType(defaultChartType));
  const [defaultWidth, setDefaultWidth] = useState(6);
  const [defaultHeight, setDefaultHeight] = useState(4);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [publishStatus, setPublishStatus] = useState<PublishStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    if (open && reportId) {
      setLoadingStatus(true);
      setError(null);
      setSuccess(null);
      reportBuilderService.getPublishStatus(reportId).then((status) => {
        setPublishStatus(status);
        if (status.is_published && status.data) {
          setTitle(status.data.title);
          setDescription(status.data.description || '');
          setChartType(toWidgetChartType(status.data.chart_type));
          setDefaultWidth(status.data.layout_default_width || 6);
          setDefaultHeight(status.data.layout_default_height || 4);
        } else {
          setTitle(reportName);
          setDescription(reportDescription);
          setChartType(toWidgetChartType(defaultChartType));
          setDefaultWidth(6);
          setDefaultHeight(4);
        }
      }).catch(() => {
        setPublishStatus(null);
      }).finally(() => setLoadingStatus(false));
    }
  }, [open, reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);
    setSuccess(null);
    try {
      if (publishStatus?.is_published) {
        await reportBuilderService.updatePublishedWidget(reportId, {
          title: title.trim(),
          description: description.trim(),
          chart_type: chartType,
          layout_default_width: defaultWidth,
          layout_default_height: defaultHeight,
        });
        setSuccess('Widget updated successfully');
        showToast.success('Widget updated', { description: `"${title.trim()}" has been updated successfully.` });
      } else {
        await reportBuilderService.publishAsWidget(reportId, {
          title: title.trim(),
          description: description.trim(),
          chart_type: chartType,
          layout_default_width: defaultWidth,
          layout_default_height: defaultHeight,
        });
        setSuccess('Report published as widget');
        showToast.success('Widget published', { description: `"${title.trim()}" is now available on dashboards.` });
      }
      setPublishStatus({ is_published: true, data: { id: '', title: title.trim(), description: description.trim(), chart_type: chartType, scoping: '', layout_default_width: defaultWidth, layout_default_height: defaultHeight, is_enabled: true } });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to publish';
      setError(msg);
      showToast.error('Failed to publish', { description: msg });
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setUnpublishing(true);
    setError(null);
    setSuccess(null);
    try {
      await reportBuilderService.unpublishWidget(reportId);
      setPublishStatus({ is_published: false });
      setSuccess('Widget unpublished');
      showToast.success('Widget unpublished', { description: 'The widget has been removed from dashboards.' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to unpublish';
      setError(msg);
      showToast.error('Failed to unpublish', { description: msg });
    } finally {
      setUnpublishing(false);
    }
  };

  const isAlreadyPublished = publishStatus?.is_published === true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isAlreadyPublished ? 'Update Published Widget' : 'Publish as Widget'}</DialogTitle>
          <DialogDescription>
            {isAlreadyPublished
              ? 'This report is published as a dashboard widget. You can update its settings or unpublish it.'
              : 'Make this report available as a widget in the dashboard template library. Changes to the report will automatically reflect in all dashboards using it.'}
          </DialogDescription>
        </DialogHeader>

        {loadingStatus ? (
          <div className="flex items-center justify-center py-6">
            <CircleNotch size={24} className="animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Widget Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Widget title"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Chart Type</label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="table">Table</option>
                <option value="bar">Bar</option>
                <option value="line">Line</option>
                <option value="pie">Pie</option>
                <option value="donut">Donut</option>
                <option value="area">Area</option>
                <option value="horizontal_bar">Horizontal Bar</option>
                <option value="stacked_bar">Stacked Bar</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Width</label>
                <select
                  value={defaultWidth}
                  onChange={(e) => setDefaultWidth(Number(e.target.value))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {[3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((w) => (
                    <option key={w} value={w}>{w} columns</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Height</label>
                <select
                  value={defaultHeight}
                  onChange={(e) => setDefaultHeight(Number(e.target.value))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {[2, 3, 4, 5, 6, 7, 8].map((h) => (
                    <option key={h} value={h}>{h} rows</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            {success && (
              <p className="text-sm text-green-600">{success}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <div className="flex w-full items-center justify-between">
            <div>
              {isAlreadyPublished && (
                <button
                  onClick={handleUnpublish}
                  disabled={unpublishing}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {unpublishing ? <CircleNotch size={14} className="animate-spin" /> : <Trash size={14} />}
                  Unpublish
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onOpenChange(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {success ? 'Close' : 'Cancel'}
              </button>
              {!success && (
                <button
                  onClick={handlePublish}
                  disabled={!title.trim() || publishing || loadingStatus}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {publishing ? <CircleNotch size={14} className="animate-spin" /> : <CloudArrowUp size={14} />}
                  {isAlreadyPublished ? 'Update Widget' : 'Publish'}
                </button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
